import React, { memo, useEffect, useState } from "react";
import { Box, Link, Tooltip } from "@mui/material";
import apiClient from "../services/apiClient";

// Release version is explicit. A build/package version must never announce 1.0.0 by default.
const RELEASE_VERSION = String(process.env.REACT_APP_RELEASE_VERSION || "").trim();
const APP_VERSION = RELEASE_VERSION ? `v${RELEASE_VERSION}` : "Pre-1.0";
const CURRENT_YEAR = new Date().getFullYear();
const BRAND_NAME = "Elite Core";

const FOOTER_LINKS = Object.freeze([
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
  { label: "Help", href: "/help" },
  { label: "Updates", href: "/update-log" },
  { label: "Status", href: "/status" },
]);

const FONT = '\"Nunito\", \"Helvetica\", \"Arial\", sans-serif';

const formatUpdateDate = (value) => {
  if (!value) return "";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  const date = new Date(dateOnly ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: dateOnly ? "UTC" : undefined,
    year: date.getFullYear() === CURRENT_YEAR ? undefined : "numeric",
  });
};

const sx = {
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 1,
    px: 3,
    py: 1.25,
    minHeight: 44,
    flexShrink: 0,
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  copyright: {
    fontSize: "0.72rem",
    fontWeight: 600,
    fontFamily: FONT,
    color: "#94a3b8",
    letterSpacing: "0.02em",
    userSelect: "none",
    lineHeight: 1.5,
  },
  brandName: {
    fontWeight: 800,
    fontFamily: FONT,
    color: "#475569",
  },
  dash: {
    color: "#cbd5e1",
    mx: 0.5,
    userSelect: "none",
  },
  rightGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 0,
  },
  latestUpdate: {
    display: "inline-block",
    verticalAlign: "middle",
    maxWidth: { xs: 220, sm: 360 },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.7rem",
    fontWeight: 700,
    fontFamily: FONT,
    color: "#64748b",
    lineHeight: 1.5,
  },
  latestLabel: { color: "#94a3b8", fontWeight: 600, mr: 0.5 },
  latestDate: { color: "#94a3b8", fontWeight: 600, ml: 0.5 },
  linkWrapper: { display: "flex", alignItems: "center" },
  dot: {
    mx: 1,
    fontSize: "0.45rem",
    color: "#e2e8f0",
    userSelect: "none",
    lineHeight: 1,
  },
  link: {
    fontSize: "0.72rem",
    fontWeight: 600,
    fontFamily: FONT,
    letterSpacing: "0.02em",
    color: "#94a3b8",
    textDecoration: "none !important",
    transition: "color 0.15s ease",
    "&:hover": { color: "#334155" },
    "&:focus-visible": {
      outline: "2px solid #e03131",
      outlineOffset: 2,
      borderRadius: "3px",
    },
  },
  rule: {
    width: "1px",
    height: 12,
    backgroundColor: "#e2e8f0",
    mx: 1.5,
    flexShrink: 0,
  },
  versionBadge: {
    px: 1,
    py: "2px",
    borderRadius: "6px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: "0.65rem",
    fontWeight: 800,
    fontFamily: '\"Courier New\", Courier, monospace',
    color: "#64748b",
    letterSpacing: "0.05em",
    userSelect: "none",
    lineHeight: 1.6,
  },
};

function AppFooter() {
  const [latestUpdate, setLatestUpdate] = useState(null);

  useEffect(() => {
    let mounted = true;
    apiClient
      .get("/api/platform/system/updates/public/latest", { timeout: 8000 })
      .then((response) => {
        if (mounted && response?.data?.success) {
          setLatestUpdate(response.data.latest || null);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Box component="footer" sx={sx.footer}>
      <Box sx={sx.copyright}>
        © {CURRENT_YEAR}{" "}
        <Box component="span" sx={sx.brandName}>{BRAND_NAME}</Box>
        <Box component="span" sx={sx.dash}>—</Box>
        All rights reserved.
      </Box>

      <Box sx={sx.rightGroup} component="nav" aria-label="Footer navigation">
        {latestUpdate ? (
          <>
            <Tooltip title={latestUpdate.summary || latestUpdate.title} arrow>
              <Link
                href={`/update-log#update-${encodeURIComponent(latestUpdate.id)}`}
                sx={{ ...sx.latestUpdate, textDecoration: "none", "&:hover": { color: "#334155", textDecoration: "underline" } }}
                aria-label={`Read latest update: ${latestUpdate.title}`}
              >
                <Box component="span" sx={sx.latestLabel}>Latest:</Box>
                {latestUpdate.title}
                <Box component="span" sx={sx.latestDate}>
                  · {formatUpdateDate(latestUpdate.date || latestUpdate.publishedAt)}
                </Box>
              </Link>
            </Tooltip>
            <Box sx={sx.rule} aria-hidden="true" />
          </>
        ) : null}

        {FOOTER_LINKS.map(({ label, href }, idx) => (
          <Box key={label} sx={sx.linkWrapper}>
            {idx > 0 && <Box component="span" sx={sx.dot} aria-hidden="true">●</Box>}
            <Link href={href} sx={sx.link}>{label}</Link>
          </Box>
        ))}

        <Box sx={sx.rule} aria-hidden="true" />
        <Box sx={sx.versionBadge} aria-label={`Elite Core release status ${APP_VERSION}`}>
          {APP_VERSION}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(AppFooter);
